import type { PutCustomerSubscription } from "@/server-types";
import {
  toStoredCompanyShareValue,
  type CompanyShareType,
} from "@/utils/subscription-shares";
import type {
  PlanLimitation,
  PlanServiceDraft,
} from "./CustomSubscriptionServicesEditor";

export type SettingsFormValues = {
  name: string;
  isRecurring: boolean;
  autoRenewOnVisitExhaustion: boolean;
  price: number;
  taxPercentage: number | null;
  shouldIssueEstimates: boolean;
  shouldReceiveEstimatesOnEmail: boolean;
  estimateOrganizationId: string | null;
  companySplitEnabled: boolean;
  payerCompanyId: number | null;
  companyShareType: CompanyShareType;
  companyShareValue: number;
  companyShouldReceiveEstimatesOnEmail: boolean;
  allLocations: boolean;
  locationIds: number[];
  limitations: PlanLimitation[];
  subscriptionServices: PlanServiceDraft[];
};

export type SettingsBodyContext = {
  isCustomSubscription: boolean;
  hasAllTimeframeLimit: boolean;
  hasLocationChanges: boolean;
  hasPlanChanges: boolean;
  hasActiveFiscalizations: boolean;
  canIssueEstimates: boolean;
  offersBillingNotifications: boolean;
};

export function buildSettingsUpdateBody(
  values: SettingsFormValues,
  context: SettingsBodyContext,
): PutCustomerSubscription["body"] {
  const trimmedName = values.name.trim();
  const hasCompanySplit =
    values.companySplitEnabled && values.payerCompanyId != null;

  const companySplit = (() => {
    // The server rejects a partial split, and clearing it takes explicit nulls rather than an omission.
    return hasCompanySplit
      ? {
          payerCompanyId: values.payerCompanyId,
          companyShareType: values.companyShareType,
          companyShareValue: toStoredCompanyShareValue(
            values.companyShareType,
            values.companyShareValue,
          ),
          companyShouldReceiveEstimatesOnEmail:
            values.companyShouldReceiveEstimatesOnEmail,
        }
      : {
          payerCompanyId: null,
          companyShareType: null,
          companyShareValue: null,
          companyShouldReceiveEstimatesOnEmail: false,
        };
  })();

  return {
    isRecurring: values.isRecurring,
    // Renewing on exhaustion needs a recurring package to renew and a period-total limit to exhaust.
    autoRenewOnVisitExhaustion:
      values.isRecurring &&
      (!context.isCustomSubscription || context.hasAllTimeframeLimit) &&
      values.autoRenewOnVisitExhaustion,
    priceCents: Math.round(values.price * 100),
    ...(context.isCustomSubscription && {
      name: trimmedName.length > 0 ? trimmedName : null,
    }),
    ...(context.hasLocationChanges && {
      allLocations: values.allLocations,
      locationIds: values.allLocations ? undefined : values.locationIds,
    }),
    ...(context.hasPlanChanges && {
      limitations: values.limitations,
      subscriptionServices: values.subscriptionServices.map((service) => ({
        serviceId: service.serviceId,
        limitations: service.limitations,
      })),
    }),
    ...(context.hasActiveFiscalizations &&
      context.isCustomSubscription && {
        taxPercentage: values.taxPercentage ?? undefined,
      }),
    ...(context.offersBillingNotifications && {
      shouldReceiveEstimatesOnEmail: values.shouldReceiveEstimatesOnEmail,
    }),
    ...(context.canIssueEstimates && {
      shouldIssueEstimates: values.shouldIssueEstimates,
      estimateOrganizationId:
        values.estimateOrganizationId != null &&
        values.estimateOrganizationId.length > 0
          ? values.estimateOrganizationId
          : null,
    }),
    ...companySplit,
  };
}
